import { useMemo } from 'react';
import { newShortId } from '@beamible/uuid';
import { Action, DesignEntity, PolicyLevel } from '@/lib/enums';
import getAssigned from '../get/assigned';
import deriveNew from '../relationships/deriveNew';
import useRoles from '../use/roles';

function activityOwnerExists(activity, snapshot) {
  if (!activity.owner_type) {
    return false;
  }

  const plural = DesignEntity.toPlural(activity.owner_type);
  return Object.hasOwn(snapshot.entities[plural], activity.owner_uuid);
}

/**
 * Short-circuit static functions
 */
function alwaysFalse() {
  return false;
}

function alwaysTrue() {
  return true;
}

/**
 *
 * @param {Object} me
 * @param {String} level
 * @param {Boolean} isHistorical
 * @param {Object} snapshot
 * @param {*} lookup
 * @returns
 */
export default function useCreate(me, level, isHistorical, snapshot, lookup) {
  const myPersonId = me?.person?.uuid;
  const myRoles = useRoles(me);
  const myAssigned = useMemo(() => {
    return getAssigned(me, snapshot);
  }, [myPersonId, snapshot]);

  // historical is always restricted
  if (isHistorical) {
    return alwaysFalse;
  }

  // admin is always allowed
  if (level === PolicyLevel.ADMIN) {
    return alwaysTrue;
  }

  // calculate
  return (entity, alternative = null) => {
    const isDefault = !alternative;

    const subject = isDefault ? snapshot : alternative;
    const assigned = isDefault ? myAssigned : getAssigned(me, subject);
    const supported = myRoles[DesignEntity.GROUP].byRole[PolicyLevel.SUPPORTER];

    const isEmptyEntity = !entity || Object.values(entity).length < 2;
    const isGroup = entity?.__type === DesignEntity.GROUP;
    const isActivity = entity?.__type === DesignEntity.ACTIVITY;

    // if we don't know the entities properties yet, allow managers and supporters to create
    const isSupporter = supported.size > 0;
    if (isEmptyEntity) {
      return assigned.isManager || (isSupporter && !isGroup);
    }

    // checking permission for an activity where the owner role isn't yet in the snapshot
    if (isActivity && !activityOwnerExists(entity, subject)) {
      return false;
    }

    // otherwise check the relationship the user will have with the new entity
    entity.uuid = entity.uuid ?? newShortId();
    const relationship = deriveNew(assigned.roles, supported, entity, subject);

    return lookup([
      {
        level,
        relationship,
        entityType: entity.__type.toLowerCase(),
        property: null,
        action: Action.CREATE,
      },
    ]);
  };
}
